import React, { useEffect, useState } from 'react';
import { TileBox } from './tile-box/TileBox';
import { t } from 'i18next';
import { AllBrandsStatsResponse } from '../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import { getTextForVisibility } from '../../../../shared/utils/utils';

interface ProjectStatsTilesProps {
  projectGeneralData: AllBrandsStatsResponse | null;
  isLoading: boolean;
  // When provided (V2 / NEW_STATS), the Visibility tile shows this % value
  // (responses mentioning the brand / total responses). When omitted (V1),
  // the tile falls back to the qualitative text derived from brandScore.
  visibilityPercent?: number | null;
  // V2 / NEW_STATS: show Average Position instead of Median Position in the middle tile.
  useAveragePosition?: boolean;
  // Fresh project still collecting its first data → tiles show a rotating "what we're doing" message.
  collecting?: boolean;
  // Statystyki marek jeszcze się liczą po włączeniu nazwy — ten sam wygląd co `collecting`, inne komunikaty.
  recomputing?: boolean;
}

// Rotating "what we're doing right now" messages shown across the tiles while data collects. Each tile
// shows a different one (offset by its position) and they all advance together — livelier than a spinner.

const RECOMPUTING_MESSAGES = ['statsRecomputingMsgPositions', 'statsRecomputingMsgVisibility'];

const COMPUTING_MESSAGES = [
  'computingMsgResponses',
  'computingMsgAnalyzing',
  'computingMsgSov',
  'computingMsgSources',
  'computingMsgBrandScore',
  'computingMsgDashboard',
];
const COMPUTING_ROTATE_MS = 2600;

const ProjectStatsTiles: React.FC<ProjectStatsTilesProps> = ({
  projectGeneralData,
  isLoading,
  visibilityPercent,
  useAveragePosition = false,
  collecting = false,
  recomputing = false,
}) => {
  const hasData = projectGeneralData !== null && !isLoading;

  const isComputing = collecting || recomputing;
  const messages = recomputing ? RECOMPUTING_MESSAGES : COMPUTING_MESSAGES;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isComputing) return;
    const id = setInterval(() => setTick((s) => s + 1), COMPUTING_ROTATE_MS);
    return () => clearInterval(id);
  }, [isComputing]);

  // Message for the tile at position `index`, offset so the tiles never show the same text at once.
  const computingMessage = (index: number): string | undefined =>
    isComputing ? t(messages[(tick + index) % messages.length]) : undefined;

  const usePercentVisibility = visibilityPercent !== undefined && visibilityPercent !== null;
  const visibilityText = projectGeneralData ? getTextForVisibility(projectGeneralData.brandScore) : '';
  return (
    <div className="tiles tiles--page">
      <TileBox
        hasData={hasData}
        collecting={isComputing}
        computingMessage={computingMessage(0)}
        label="brandScore"
        tooltipText="brandScoreTooltip"
        value={projectGeneralData ? Math.round(projectGeneralData.brandScore) : 0}
        trendValue={projectGeneralData?.trends.visibilityTrend || 0}
        suffix="%"
      />
      {useAveragePosition ? (
        <TileBox
          hasData={hasData}
          collecting={isComputing}
          computingMessage={computingMessage(1)}
          label="averagePosition"
          tooltipText="averagePositionTooltip"
          value={projectGeneralData ? projectGeneralData.position : 0}
          trendValue={projectGeneralData?.trends.positionTrend || 0}
        />
      ) : (
        <TileBox
          hasData={hasData}
          collecting={isComputing}
          computingMessage={computingMessage(1)}
          label="medianPosition"
          tooltipText="medianPositionTooltip"
          value={projectGeneralData ? projectGeneralData.medianPosition : 0}
          trendValue={projectGeneralData?.trends.medianTrend || 0}
        />
      )}
      {usePercentVisibility ? (
        <TileBox
          hasData={hasData}
          collecting={isComputing}
          computingMessage={computingMessage(2)}
          label="visibility"
          tooltipText="visibilityTooltip"
          value={t(getTextForVisibility(visibilityPercent as number))}
          subValue={`${Math.round(visibilityPercent as number)}%`}
          trendValue={0}
        />
      ) : (
        <TileBox
          hasData={hasData}
          collecting={isComputing}
          computingMessage={computingMessage(2)}
          label="visibility"
          tooltipText={visibilityText + 'Tooltip'}
          value={visibilityText ? t(visibilityText) : '-'}
          trendValue={0}
        />
      )}
    </div>
  );
};

export default ProjectStatsTiles;
